// Sugalarm — cycle 365: the "this phone can't be paged" activation prompt.
//
// A non-blocking card (never a takeover) that appears when the SERVER says
// this install has a delivery gap. It exists because of a real field case:
// an iPhone signed into a live household account, streaming readings and
// showing a glucose tile, with no APNs token at all — so no alarm could
// ever reach it, and nothing on any screen said so.
//
// WHY THE DISMISS RULES DIFFER FROM EVERY OTHER NUDGE HERE. The cycle-316
// alarm tour is once-per-device and skippable, and skipping it is exactly
// how a phone ends up permanently unpageable. So:
//   • critical (can't be paged)  → "Not now" holds for THIS SESSION only.
//     There is deliberately no "don't remind me again" for this state.
//   • advisory (glance-only)     → "Don't remind me" holds persistently;
//     a missing widget is a comfort issue, not a safety one.
//
// Alarm-safe by construction: renders null while an episode is live, and
// sits at z-index 30 — below AlarmSilenceGuard (60), UrgentOverlay (100)
// and ReminderOverlay (9000) — so an alarm and its Snooze/Stop always win
// the screen (the cycle-81 control floor).

import { useEffect, useState } from "react";

import { Button } from "../../components/ui/Button";
import * as notificationSettings from "../../lib/notificationSettings";
import {
  WIDGET_STEPS,
  gapCopy,
  primaryActionFor,
  primaryActionLabel,
  shouldShowActivationPrompt,
  type ActivationSeverity,
  type ActivationVerdict,
} from "../../lib/activation";
import {
  enableNativePush,
  isNativePushEnabledLocally,
} from "../../lib/nativePush";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import styles from "./ActivationPrompt.module.css";

const ADVISORY_OPT_OUT_KEY = "sugalarm.activationAdvisoryOptOut";

function advisoryOptedOut(): boolean {
  try {
    return localStorage.getItem(ADVISORY_OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

function setAdvisoryOptOut(): void {
  try {
    localStorage.setItem(ADVISORY_OPT_OUT_KEY, "1");
  } catch {
    /* ignore */
  }
}

export interface ActivationPromptProps {
  verdict: ActivationVerdict | null;
  episodeLive: boolean;
  variant: FrameVariant;
  onAlertId: (alertId: string) => void;
  /** Re-run the guard after the user acts, so the card clears itself the
   *  moment the gap is actually closed (rather than on the next resume). */
  onRecheck: () => void;
}

export default function ActivationPrompt({
  verdict,
  episodeLive,
  variant,
  onAlertId,
  onRecheck,
}: ActivationPromptProps) {
  const [dismissedSeverity, setDismissedSeverity] =
    useState<ActivationSeverity | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // A fresh, WORSE verdict re-opens a card the user dismissed: dismissing
  // an advisory nag must not also suppress a later "you can't be paged".
  useEffect(() => {
    if (verdict?.severity === "critical" && dismissedSeverity === "advisory") {
      setDismissedSeverity(null);
    }
  }, [verdict?.severity, dismissedSeverity]);

  const show = shouldShowActivationPrompt({
    verdict,
    alarmLive: episodeLive,
    dismissedSessionSeverity: dismissedSeverity,
    advisoryOptedOut: advisoryOptedOut(),
  });
  if (!show || !verdict) return null;

  const critical = verdict.severity === "critical";
  // Cycle 453.1: a verdict with no `gaps` list reads as NO gaps. Reading
  // `.length` off `undefined` threw here and took the whole shell down —
  // on a bedside kiosk, a blank screen can hide an alarm. The server always
  // sends a list; this holds even when a reply is malformed.
  const gaps = Array.isArray(verdict.gaps) ? verdict.gaps : [];
  const primaryGap = gaps[0] ?? null;

  // Cycle 365.2: the action comes from the GAP, not the severity. Branching
  // on severity is what produced a "Turn on alerts" button on a `no_widget`
  // card that called enableNativePush(), found permission already granted,
  // and silently re-rendered the same card — a dead button.
  const action = primaryActionFor(gaps);

  async function handleActivate() {
    // `instructions` has no in-app action to run: adding a widget is a
    // manual Home/Lock Screen gesture with no iOS API. Acknowledging it
    // dismisses rather than pretending to do something.
    if (action === "instructions") {
      setDismissedSeverity(verdict!.severity);
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      if (action === "open_settings") {
        // Once permission is denied — or the toggle lives in iOS Settings
        // (Critical Alerts, Live Activities) — re-requesting in-app is a
        // silent no-op, so Settings is the only real path.
        await notificationSettings.openAppNotificationSettings();
      } else {
        const permission = await enableNativePush({ onAlertId });
        if (permission !== "granted") setFailed(true);
      }
    } catch (err) {
      console.warn("[activation] activate failed", err);
      setFailed(true);
    } finally {
      setBusy(false);
      onRecheck();
    }
  }

  const title = critical
    ? "Alarms can't reach this phone"
    : "Your glucose glance needs a nudge";

  return (
    <div
      className={`${styles.card} ${variant === "mobile" ? styles.mobile : styles.kiosk} ${
        critical ? styles.critical : styles.advisory
      }`}
      role="region"
      aria-label={title}
    >
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden="true">
          {critical ? "🔔" : "📲"}
        </span>
        <div className={styles.headText}>
          <div className={styles.title}>{title}</div>
          <div className={styles.sub}>
            {primaryGap ? gapCopy(primaryGap) : "Alert delivery needs attention."}
          </div>
        </div>
      </div>

      {gaps.length > 1 && (
        <ul className={styles.list}>
          {gaps.slice(1).map((g) => (
            <li key={g}>{gapCopy(g)}</li>
          ))}
        </ul>
      )}

      {/* The app cannot add a widget for you, so say how instead of
          offering a button that can't work. */}
      {action === "instructions" && (
        <ul className={styles.list}>
          {WIDGET_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ul>
      )}

      {failed && (
        <div className={styles.failed}>
          Still not set up. Open iOS Settings → Notifications for Sugar Alarm and
          allow alerts.
        </div>
      )}

      <div className={styles.actions}>
        <Button onClick={handleActivate} disabled={busy}>
          {busy ? "Working…" : primaryActionLabel(action)}
        </Button>
        {action !== "instructions" && (
          <button
            type="button"
            className={styles.later}
            onClick={() => setDismissedSeverity(verdict.severity)}
          >
            Not now
          </button>
        )}
        {!critical && (
          <button
            type="button"
            className={styles.later}
            onClick={() => {
              setAdvisoryOptOut();
              setDismissedSeverity(verdict.severity);
            }}
          >
            Don't remind me
          </button>
        )}
      </div>

      {/* Diagnostic breadcrumb for support: shows whether the local arm
          flag is even set, which is the switch that gates registration. */}
      {critical && !isNativePushEnabledLocally() && (
        <div className={styles.note}>
          Alerts have never been switched on for this install.
        </div>
      )}
    </div>
  );
}
